import { TITLE_MIN, TITLE_MAX, DESC_MIN, DESC_MAX } from './seoAudit';

/**
 * Dynamic-page SEO builder.
 *
 * Static pages resolve their title/description from src/lib/seo-config.ts.
 * Dynamic pages — careers job postings, insights articles and resources — do
 * not exist in that map, so they build their meta here from a TEMPLATE plus an
 * optional per-item override. The runtime page and the build-time prerenderer
 * both call these helpers, so the two can never drift.
 *
 * Rules (enforced by the build audit):
 *   • title        50–60 characters, brand last
 *   • description  140–158 characters
 * Both are always clamped/padded into range by construction, so no dynamic page
 * can ever ship an out-of-range value.
 *
 * A per-item override (seo.metaTitle / seo.metaDescription) is honoured only
 * when it already sits inside the range; otherwise the template wins — this
 * keeps legacy CMS values from breaking the audit.
 */

export type DynamicSeoKind = 'article' | 'resource' | 'job';

export interface BuiltSeo {
  title: string;
  description: string;
  keywords: string;
}

/** Collapses whitespace and trims. */
export function cleanText(value: string | null | undefined): string {
  return String(value == null ? '' : value)
    .replace(/\s+/g, ' ')
    .trim();
}

/** Truncates to <= maxLen on a word boundary, stripping trailing punctuation. */
export function truncateWords(text: string, maxLen: number): string {
  const t = cleanText(text);
  if (t.length <= maxLen) return t;
  let cut = t.slice(0, maxLen);
  const sp = cut.lastIndexOf(' ');
  if (sp >= Math.min(20, Math.floor(maxLen * 0.5))) cut = cut.slice(0, sp);
  return cut.replace(/[\s\-–—:,;.|]+$/, '').trim();
}

/**
 * Brand-last title suffixes, ascending by length. The windows they cover for
 * the body (min-1-len … max-1-len) overlap so that any body length in
 * [25, 49] lands inside 50–60 with exactly one suffix.
 */
const TITLE_LADDER: Record<DynamicSeoKind, string[]> = {
  article: ['| Cendien', '| Cendien Insights', '| Cendien IT Consulting'],
  resource: ['| Cendien', '| Cendien Resources', '| Cendien IT Consulting'],
  job: ['| Cendien', '| Cendien Careers', '| Cendien IT Consulting'],
};

/** Neutral filler tokens used only when a body is unusually short. */
const PAD_TOKENS: Record<DynamicSeoKind, string[]> = {
  article: ['Insights', 'Explained', 'Guide'],
  resource: ['Resource', 'Guide', 'Overview'],
  job: ['Role', 'Careers', 'Details'],
};

function padBody(body: string, target: number, kind: DynamicSeoKind): string {
  let b = cleanText(body);
  let i = 0;
  while (b.length < target && i < 12) {
    const token = PAD_TOKENS[kind][i % PAD_TOKENS[kind].length];
    b = b ? `${b} ${token}` : token;
    i += 1;
  }
  return b;
}

/** Builds an in-range, brand-last title from a body string. */
export function fitTitle(body: string, kind: DynamicSeoKind): string {
  const ladder = TITLE_LADDER[kind];
  let b = cleanText(body);
  const fits = (len: number, suffix: string) => {
    const total = len + 1 + suffix.length;
    return total >= TITLE_MIN && total <= TITLE_MAX;
  };

  // Exact body with a suffix that lands in range.
  for (const suffix of ladder) if (fits(b.length, suffix)) return `${b} ${suffix}`;

  // Body too long → shrink to the widest budget (shortest suffix).
  const shortest = ladder[0];
  const maxBody = TITLE_MAX - 1 - shortest.length;
  if (b.length > maxBody) b = truncateWords(b, maxBody);
  for (const suffix of ladder) if (fits(b.length, suffix)) return `${b} ${suffix}`;

  // Body too short → pad to the tightest lower bound (longest suffix).
  const longest = ladder[ladder.length - 1];
  const minBody = TITLE_MIN - 1 - longest.length;
  if (b.length < minBody) b = padBody(b, minBody, kind);
  for (const suffix of ladder) if (fits(b.length, suffix)) return `${b} ${suffix}`;

  // Absolute fallback (should never trigger).
  let full = `${b} ${shortest}`;
  if (full.length > TITLE_MAX) full = full.slice(0, TITLE_MAX).replace(/\s+\S*$/, '').trim();
  if (full.length < TITLE_MIN) full = padBody(full, TITLE_MIN, kind).slice(0, TITLE_MAX).trim();
  return full;
}

/** Neutral, non-fabricated tails used to lengthen short descriptions. */
const DESC_TAILS = [
  "Learn how Cendien delivers IT consulting, ERP, cloud, AI and staffing services across Dallas–Fort Worth and beyond.",
  'Cendien is an IT consulting, ERP, cloud, AI and staffing firm serving Dallas–Fort Worth and clients across the United States.',
];

/** Builds an in-range description from any source text. */
export function fitDescription(text: string | null | undefined): string {
  let d = cleanText(text);
  if (d.length > DESC_MAX) d = truncateWords(d, DESC_MAX);
  let i = 0;
  while (d.length < DESC_MIN && i < DESC_TAILS.length) {
    d = `${d} ${DESC_TAILS[i]}`.trim();
    if (d.length > DESC_MAX) d = truncateWords(d, DESC_MAX);
    i += 1;
  }
  if (d.length < DESC_MIN) d = truncateWords(`${d} ${DESC_TAILS[0]}`.trim(), DESC_MAX);
  return d;
}

/**
 * How-to category / sub-category title.
 *
 * A how-to page is a collection of videos, so the title always names the label
 * and appends the longest natural descriptor that still lands inside 50–60 with
 * a brand-last suffix. The descriptor cascade (full → tutorials → guides →
 * bare) plus the suffix ladder guarantees EVERY label fits without rewording,
 * and stays readable instead of being truncated mid-phrase.
 */
const HOWTO_SUFFIXES = ['| Cendien', '| Cendien Videos', '| Cendien How-Tos'];
const HOWTO_DESCRIPTORS = [
  ' How-To Videos & Step-by-Step Guides',
  ' How-To Videos & Tutorials',
  ' How-To Videos & Guides',
  ' How-To Videos',
];

export function fitHowToTitle(label: string): string {
  const name = cleanText(label);
  for (const descriptor of HOWTO_DESCRIPTORS) {
    const body = `${name}${descriptor}`;
    for (const suffix of HOWTO_SUFFIXES) {
      const title = `${body} ${suffix}`;
      if (title.length >= TITLE_MIN && title.length <= TITLE_MAX) return title;
    }
  }
  // Fallback for an extreme label: the generic engine truncates/pads into range.
  return fitTitle(`${name} How-To Videos & Step-by-Step Guides`, 'article');
}

/** How-to category / sub-category description, clamped to 140–158. */
export function fitHowToDescription(label: string): string {
  return fitDescription(
    `Step-by-step ${cleanText(label)} how-to videos from Cendien — practical walkthroughs with written steps, full transcripts and clear, task-by-task guidance.`,
  );
}

function validOverride(value: string | undefined, min: number, max: number): string | null {
  const v = cleanText(value);
  if (!v) return null;
  return v.length >= min && v.length <= max ? v : null;
}

function keywordsFrom(values: (string | undefined | null)[]): string {
  return values.filter(Boolean).map((v) => cleanText(v as string)).join(', ');
}

/** Remote / Hybrid / On-site, derived from a location string. */
export function workModeFromLocation(location?: string): string {
  const l = String(location || '').toLowerCase();
  if (l.includes('hybrid')) return 'Hybrid';
  if (/remote|telecommute|work from home|virtual/.test(l)) return 'Remote';
  return 'On-site';
}

/** "{title} — {location} ({mode})" — unique per posting. */
export function jobDisplayTitle(title: string, location: string, workMode?: string): string {
  const mode = workMode || workModeFromLocation(location);
  const loc = cleanText(location).replace(/\s*\((hybrid|remote)\)\s*/i, ' ').trim();
  return `${cleanText(title)} — ${loc} (${mode})`;
}

export interface ArticleSeoInput {
  title: string;
  excerpt?: string;
  category?: string;
  tags?: string[];
  overrideTitle?: string;
  overrideDescription?: string;
}

export function buildArticleSeo(input: ArticleSeoInput): BuiltSeo {
  const title = validOverride(input.overrideTitle, TITLE_MIN, TITLE_MAX) || fitTitle(input.title, 'article');
  const description =
    validOverride(input.overrideDescription, DESC_MIN, DESC_MAX) ||
    fitDescription(input.excerpt || input.title);
  const keywords = keywordsFrom([
    ...(input.tags || []),
    input.category,
  ]) || 'IT insights, technology articles, Cendien';
  return { title, description, keywords };
}

export interface ResourceSeoInput {
  title: string;
  type?: string;
  topic?: string;
  excerpt?: string;
  description?: string;
  tags?: string[];
  overrideTitle?: string;
  overrideDescription?: string;
}

export function buildResourceSeo(input: ResourceSeoInput): BuiltSeo {
  const title = validOverride(input.overrideTitle, TITLE_MIN, TITLE_MAX) || fitTitle(input.title, 'resource');
  const description =
    validOverride(input.overrideDescription, DESC_MIN, DESC_MAX) ||
    fitDescription(input.excerpt || input.description || input.title);
  const keywords = keywordsFrom([
    ...(input.tags || []),
    input.type,
    input.topic,
  ]) || 'IT resources, guides, Cendien';
  return { title, description, keywords };
}

export interface JobSeoInput {
  title: string;
  location: string;
  workMode?: string;
  department?: string;
  description?: string;
  overrideTitle?: string;
  overrideDescription?: string;
}

export function buildJobSeo(input: JobSeoInput): BuiltSeo {
  const mode = input.workMode || workModeFromLocation(input.location);
  const body = jobDisplayTitle(input.title, input.location, mode);
  const title = validOverride(input.overrideTitle, TITLE_MIN, TITLE_MAX) || fitTitle(body, 'job');
  const description =
    validOverride(input.overrideDescription, DESC_MIN, DESC_MAX) ||
    fitDescription(
      `${input.title} role at Cendien in ${cleanText(input.location)} (${mode}). ${input.description || ''}`,
    );
  const loc = cleanText(input.location).replace(/\s*\((hybrid|remote)\)\s*/i, ' ').trim();
  const keywords = keywordsFrom([
    `${input.title}`,
    `${mode} ${loc} jobs`,
    'Cendien careers',
    `${input.department || 'IT'} jobs`,
  ]);
  return { title, description, keywords };
}